import { useRef, useState } from 'react';
import { ModalCloseButton } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { PhotoCaptureModal, type PhotoCropArea } from '../PhotoCaptureModal';
import { VideoCaptureModal } from '../VideoCaptureModal';
import { CaptureChoice } from './CaptureChoice';

// Optional capture step between the live timer ending and the cut summary.
// Mirrors BeforePhotoStep: prompt with skip option, then transition.
//
// When an after photo/video is captured the screen pivots to "Show
// transformation" — the primary CTA. Tapping it routes to the new
// TransformationRevealStep (mode='reveal') which generates the simultaneous
// 9:16 stack (or photo-only slideshow) and offers share / save / continue.
// Skip-to-wrap-up bypasses the reveal entirely (no achievement credit).
//
// Sharing is intentionally absent from this screen — by spec it lives only
// on the reveal screen, and only when an after exists.

export function AfterPhotoStep() {
  const t = useT();
  const beforePhotoUrl = useCutTimer((s) => s.beforePhotoUrl);
  const afterPhotoUrl = useCutTimer((s) => s.afterPhotoUrl);
  const setAfterPhoto = useCutTimer((s) => s.setAfterPhoto);
  const setAfterVideo = useCutTimer((s) => s.setAfterVideo);
  const goToReveal = useCutTimer((s) => s.goToRevealFromAfter);
  const goToSummary = useCutTimer((s) => s.goToSummaryFromAfter);
  const closeModal = useCutTimer((s) => s.closeModal);

  const [captureOpen, setCaptureOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  // See BeforePhotoStep for the same ref pattern — high-fidelity
  // native source + crop area land in a ref via onCaptureNative,
  // then handleCaptured bundles them into the setAfterPhoto call
  // alongside the cropped blob.
  const pendingNativeRef = useRef<{ source: Blob; crop: PhotoCropArea } | null>(null);
  function handleCapturedNative(source: Blob, crop: PhotoCropArea) {
    pendingNativeRef.current = { source, crop };
  }
  function handleCaptured(blob: Blob) {
    const pending = pendingNativeRef.current;
    pendingNativeRef.current = null;
    setAfterPhoto(blob, pending?.source, pending?.crop);
    setAfterVideo(null); // photo path — drop any prior recorded clip
    setCaptureOpen(false);
  }

  async function handleVideoCaptured(out: {
    photoBlob: Blob;
    videoBlob: Blob;
    videoCrop: PhotoCropArea | null;
  }) {
    setAfterPhoto(out.photoBlob);
    setAfterVideo(out.videoBlob, out.videoCrop);
    setVideoOpen(false);
  }

  const haveBefore = !!beforePhotoUrl;
  const haveAfter = !!afterPhotoUrl;

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mt-2 mb-2">
          <CameraIcon className="w-3 h-3" />
          {t('cut.after.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.after.title')}</h2>
        <p className="text-white/72 text-sm leading-relaxed mt-2 max-w-sm">
          {t('cut.after.body')}
        </p>
      </div>

      <div className="p-5 space-y-3">
        {haveAfter ? (
          // After captured. Show a small preview + the transformation CTAs.
          // Primary = Show transformation (advances to reveal step), secondary
          // = Skip & wrap-up (bypasses reveal — no achievement credit). The
          // reveal step is where share + save live.
          <div className="space-y-3">
            <div className="rounded-2xl overflow-hidden border border-line">
              <img
                src={afterPhotoUrl ?? undefined}
                alt=""
                className="w-full aspect-square object-cover"
              />
            </div>
            <button
              type="button"
              onClick={goToReveal}
              className="w-full h-12 rounded-xl bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold text-[15px] inline-flex items-center justify-center gap-2 shadow-sm hover:shadow-md active:scale-[0.99] transition-all"
            >
              {haveBefore
                ? t('cut.after.showTransformation')
                : t('cut.after.showAfter')}
              <ChevronRight />
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCaptureOpen(true)}
                className="h-11 px-3 rounded-lg border border-line bg-surface text-xs font-bold text-ink-700 inline-flex items-center justify-center gap-1.5"
              >
                <RetakeIcon className="w-3.5 h-3.5" />
                {t('cut.after.retake')}
              </button>
              <button
                type="button"
                onClick={goToSummary}
                className="flex-1 h-11 rounded-lg border border-line bg-surface text-ink-700 font-bold text-sm"
              >
                {t('cut.after.skipToWrapup')}
              </button>
            </div>
            {!haveBefore && (
              <p className="text-[11px] text-ink-500 text-center leading-snug">
                {t('cut.after.noBeforeHint')}
              </p>
            )}
          </div>
        ) : (
          // No after yet. Two equal-sized photo/video options side-by-side
          // (matches the Before step), then a primary "Skip & continue" CTA.
          <>
            <div className="grid grid-cols-2 gap-2">
              <CaptureChoice
                kind="photo"
                label={t('cut.after.photoChoice')}
                onClick={() => setCaptureOpen(true)}
              />
              <CaptureChoice
                kind="video"
                label={t('cut.after.videoChoice')}
                onClick={() => setVideoOpen(true)}
              />
            </div>
            <button
              type="button"
              onClick={goToSummary}
              className="w-full h-12 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center justify-center gap-2"
            >
              {t('cut.after.skipAndContinue')}
              <ChevronRight />
            </button>
            {!haveBefore && (
              <p className="text-[11px] text-ink-500 text-center leading-snug">
                {t('cut.after.noBeforeHint')}
              </p>
            )}
          </>
        )}
      </div>

      <PhotoCaptureModal
        open={captureOpen}
        title={t('cut.after.captureTitle')}
        composeRole="after"
        onCapture={handleCaptured}
        onCaptureNative={handleCapturedNative}
        onClose={() => setCaptureOpen(false)}
      />

      <VideoCaptureModal
        open={videoOpen}
        title={t('cut.after.videoTitle')}
        composeRole="after"
        onCapture={handleVideoCaptured}
        onClose={() => setVideoOpen(false)}
      />
    </>
  );
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function RetakeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15A9 9 0 1 1 5.64 5.64L23 10" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

